import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast, ToastContainer } from 'react-toastify';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { apiRequests } from '../../shared/api/apiRequests';
import { axiosInstance } from '../../shared/api/axiosInstance';
import { useStore } from '../../shared/stores/context.js';
import './Authorization.css';

const AuthorizationPage = () => {
  const { userStore } = useStore();
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA не инициализирован');
      return;
    }

    if (!form.email || !form.password) {
      toast.error('Заполните все поля');
      return;
    }

    setIsLoading(true);

    try {
      const token = await executeRecaptcha('login');

      const originalHeaders = { ...axiosInstance.defaults.headers };
      axiosInstance.defaults.headers = {
        ...originalHeaders,
        'X-CAPTCHA-V3-TOKEN': token,
      };

      const query = apiRequests.users
        .auth(form)
        .then((res) => {
          if (res) {
            Cookies.set('sid', res.sessID);
            Cookies.set('refreshToken', res.refreshToken);
            Cookies.set('expiredAt', res.expiresAt);

            axiosInstance.defaults.headers = {
              Sessid: res.sessID,
            };

            if (axiosInstance.defaults.headers['X-CAPTCHA-V3-TOKEN']) {
              delete axiosInstance.defaults.headers['X-CAPTCHA-V3-TOKEN'];
            }

            userStore.getData();
            navigate('/');
          }
        })
        .catch((error) => {
          console.error('Authentication error:', error);
          if (axiosInstance.defaults.headers['X-CAPTCHA-V3-TOKEN']) {
            delete axiosInstance.defaults.headers['X-CAPTCHA-V3-TOKEN'];
          }
          throw error;
        })
        .finally(() => {
          setIsLoading(false);
        });

      toast.promise(query, {
        pending: 'Выполняется вход...',
        error: {
          render: ({ data }) => {
            return data.error || 'Ошибка авторизации';
          },
        },
        success: 'Добро пожаловать!',
      });
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      toast.error('Ошибка при проверке reCAPTCHA');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__left">
        <div className="auth-page__decoration auth-page__decoration--1" />
        <div className="auth-page__decoration auth-page__decoration--2" />
        <div className="auth-page__decoration auth-page__decoration--3" />
        
        <div className="auth-page__logo">
          <img src="/logotype_auth.png" alt="Alfateam" />
        </div>
        <p className="auth-page__tagline">
          Панель управления для эффективной работы с вашим бизнесом
        </p>
      </div>

      <div className="auth-page__right">
        <div className="auth-form-container">
          <div className="auth-form__header">
            <h1 className="auth-form__title">Добро пожаловать</h1>
            <p className="auth-form__subtitle">Войдите в свой аккаунт для продолжения</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="auth-form__group">
              <label className="auth-form__label">Email</label>
              <div className="auth-form__input-wrapper">
                <input
                  type="email"
                  className="auth-form__input"
                  placeholder="example@company.com"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  onKeyPress={handleKeyPress}
                  autoComplete="email"
                />
                <Mail className="auth-form__input-icon" size={20} />
              </div>
            </div>

            <div className="auth-form__group">
              <label className="auth-form__label">Пароль</label>
              <div className="auth-form__input-wrapper">
                <input
                  type="password"
                  className="auth-form__input"
                  placeholder="Введите пароль"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  onKeyPress={handleKeyPress}
                  autoComplete="current-password"
                />
                <Lock className="auth-form__input-icon" size={20} />
              </div>
            </div>

            <button
              type="submit"
              className="auth-form__submit"
              disabled={isLoading}
            >
              {isLoading ? (
                'Вход...'
              ) : (
                <>
                  <LogIn size={20} style={{ marginRight: 8 }} />
                  Войти в систему
                </>
              )}
            </button>
          </form>

          <div className="auth-form__footer">
            <p className="auth-form__footer-text">
              Защищено reCAPTCHA v3
            </p>
          </div>
        </div>
      </div>
      
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default AuthorizationPage;
